import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  ShoppingCart,
  History,
  Users,
  Settings2,
  LayoutDashboard,
  LogOut,
  ShieldCheck,
  UserCog,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";

function NavItem({ to, icon: Icon, label, testid }) {
  return (
    <NavLink
      to={to}
      data-testid={testid}
      className={({ isActive }) =>
        [
          "flex items-center gap-3 px-3 py-2 text-sm rounded-sm border border-transparent",
          "transition-colors duration-150",
          isActive
            ? "bg-zinc-950 text-white border-zinc-950"
            : "text-zinc-700 hover:bg-zinc-100 hover:text-zinc-950",
        ].join(" ")
      }
    >
      <Icon className="h-4 w-4 shrink-0" strokeWidth={1.8} />
      <span className="font-medium">{label}</span>
    </NavLink>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";

  return (
    <div className="min-h-screen bg-white text-zinc-950">
      <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] min-h-screen">
        {/* Sidebar */}
        <aside className="border-r border-zinc-200 bg-zinc-50 flex flex-col">
          <div className="px-5 py-6 border-b border-zinc-200">
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500">
              OE WMS BY G
            </div>
            <div className="mt-1 text-lg font-black tracking-tighter">
              Sepet Yöneticisi
            </div>
          </div>

          <nav className="flex-1 p-3 space-y-1">
            {isAdmin && (
              <NavItem
                to="/dashboard"
                icon={LayoutDashboard}
                label="Panel"
                testid="nav-dashboard"
              />
            )}
            <NavItem
              to="/carts"
              icon={ShoppingCart}
              label="Aktif Sepetler"
              testid="nav-carts"
            />
            <NavItem
              to="/history"
              icon={History}
              label="Taslak Geçmişi"
              testid="nav-history"
            />
            <NavItem
              to="/profile"
              icon={UserCog}
              label="Profil"
              testid="nav-profile"
            />
            {isAdmin && (
              <>
                <NavItem
                  to="/admin/users"
                  icon={Users}
                  label="Kullanıcılar"
                  testid="nav-users"
                />
                <NavItem
                  to="/settings"
                  icon={Settings2}
                  label="API Ayarları"
                  testid="nav-settings"
                />
              </>
            )}
          </nav>

          <div className="border-t border-zinc-200 p-3">
            <div className="px-3 py-2 rounded-sm bg-white border border-zinc-200">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-sm bg-zinc-950 text-white flex items-center justify-center text-xs font-bold uppercase">
                  {(user?.name || user?.email || "?").slice(0, 2)}
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-semibold truncate">
                    {user?.name}
                  </div>
                  <div className="text-[10px] text-zinc-500 truncate font-mono">
                    {user?.email}
                  </div>
                </div>
              </div>
              <div className="mt-2 flex items-center gap-1.5">
                {isAdmin && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#002FA7] border border-[#002FA7] px-1.5 py-0.5"
                    data-testid="user-role-badge"
                  >
                    <ShieldCheck className="h-3 w-3" />
                    Admin
                  </span>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-auto h-7 text-xs hover:bg-zinc-100 text-zinc-700"
                  onClick={async () => {
                    await logout();
                    navigate("/login");
                  }}
                  data-testid="logout-btn"
                >
                  <LogOut className="h-3.5 w-3.5 mr-1" /> Çıkış
                </Button>
              </div>
            </div>
          </div>
        </aside>

        {/* Main area */}
        <main className="min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
